import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Brain, TrendingUp } from 'lucide-react';
import DataGrid from '../components/DataGrid';
import { DrillCell } from '../components/DrillPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 }).format(n || 0);

/**
 * Procurement Analytics — iter171.
 *
 * All three tables (Vendor Performance, Price Fluctuation, Costing
 * Predictions) now use the BI Phase 1 `DataGrid` component which adds:
 *   • Click-header sorting (asc/desc/none)
 *   • Per-column filters (text contains / numeric range / enum multi-select)
 *   • Global cross-column search box
 *   • Pagination (25 rows/page by default)
 *   • CSV + Excel (.xlsx) export of the *filtered+sorted* view
 */
export default function ProcurementAnalytics() {
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  // iter172 — Explicit calendar-month mode (year+month) overrides the
  // rolling `months` window. null = use rolling window.
  const [pickerYear, setPickerYear]   = useState(null);
  const [pickerMonth, setPickerMonth] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = pickerYear && pickerMonth
          ? `year=${pickerYear}&month=${pickerMonth}`
          : `months=${months}`;
        const [v, m, c] = await Promise.all([
          axios.get(`${API}/procurement-analytics/vendor-performance?${q}`, { withCredentials: true }),
          axios.get(`${API}/procurement-analytics/price-fluctuation?${q}`, { withCredentials: true }),
          axios.get(`${API}/procurement-analytics/costing-predictions`, { withCredentials: true }),
        ]);
        setVendors(v.data?.vendors || []);
        setMaterials(m.data?.materials || []);
        setPredictions(c.data?.materials || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [months, pickerYear, pickerMonth]);

  // ── Vendor Performance columns ────────────────────────────────────────
  const vendorColumns = [
    { key: 'vendor',          label: 'Vendor',        accessor: v => v.vendor, type: 'text', align: 'left',
      render: v => (
        <DrillCell drill={{
          type: 'vendor-bills',
          params: { vendor_name: v.vendor, ...(pickerYear && pickerMonth ? { year: pickerYear, month: pickerMonth } : { months }) },
          title: `Bills — ${v.vendor}`,
        }} testId={`drill-vendor-${v.vendor}`}>
          <span className="text-zinc-100 font-medium">{v.vendor}</span>
        </DrillCell>
      ) },
    { key: 'spend',           label: 'Spend',         accessor: v => v.spend, type: 'number', align: 'right',
      render: v => (
        <DrillCell drill={{
          type: 'vendor-bills',
          params: { vendor_name: v.vendor, ...(pickerYear && pickerMonth ? { year: pickerYear, month: pickerMonth } : { months }) },
          title: `Spend breakdown — ${v.vendor}`,
        }}>
          <span className="text-emerald-300 font-mono">{fmt(v.spend)}</span>
        </DrillCell>
      ) },
    { key: 'bills',           label: 'Bills',         accessor: v => v.bills, type: 'number', align: 'right',
      render: v => <span className="text-zinc-400 font-mono">{v.bills}</span> },
    { key: 'materials_count', label: 'Materials',     accessor: v => v.materials_count, type: 'number', align: 'right',
      render: v => <span className="text-sky-300 font-mono">{v.materials_count}</span> },
    { key: 'on_time_pct',     label: 'On-Time %',     accessor: v => v.on_time_pct ?? -1, type: 'number', align: 'right',
      render: v => v.on_time_pct == null ? <span className="text-zinc-500">—</span>
        : <span className={`font-mono ${v.on_time_pct >= 80 ? 'text-green-400' : v.on_time_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{v.on_time_pct}%</span> },
    { key: 'avg_price_drift_pct', label: 'Price Drift', accessor: v => v.avg_price_drift_pct, type: 'number', align: 'right',
      render: v => <span className={`font-mono ${v.avg_price_drift_pct <= 5 ? 'text-green-400' : v.avg_price_drift_pct <= 15 ? 'text-amber-400' : 'text-red-400'}`}>{v.avg_price_drift_pct}%</span> },
    { key: 'last_invoice',    label: 'Last Invoice',  accessor: v => v.last_invoice || '', type: 'date', align: 'left',
      render: v => <span className="text-zinc-500 font-mono">{v.last_invoice || '—'}</span> },
    { key: 'rating',          label: 'Rating',        accessor: v => v.rating, type: 'number', align: 'center',
      render: v => (
        <div className="flex items-center justify-center gap-0.5">
          {[1, 2, 3, 4, 5].map(s => (
            <Star key={s} size={10} className={s <= Math.round(v.rating) ? 'text-amber-400 fill-amber-400' : 'text-zinc-700'} />
          ))}
          <span className="text-[10px] text-zinc-500 ml-1">{v.rating}</span>
        </div>
      ) },
  ];

  // ── Price Fluctuation columns ─────────────────────────────────────────
  const materialColumns = [
    { key: 'material', label: 'Material', accessor: m => m.material, type: 'text',
      render: m => (
        <DrillCell drill={{
          type: 'material-bills',
          params: { material_name: m.material, ...(pickerYear && pickerMonth ? { year: pickerYear, month: pickerMonth } : { months }) },
          title: `Purchase line items — ${m.material}`,
        }} testId={`drill-material-${m.material}`}>
          <span className="text-zinc-100 font-medium">{m.material}</span>
        </DrillCell>
      ) },
    { key: 'total_spend', label: 'Spend', accessor: m => m.total_spend, type: 'number', align: 'right',
      render: m => (
        <DrillCell drill={{
          type: 'material-bills',
          params: { material_name: m.material, ...(pickerYear && pickerMonth ? { year: pickerYear, month: pickerMonth } : { months }) },
          title: `Purchase line items — ${m.material}`,
        }}>
          <span className="text-emerald-300 font-mono">{fmt(m.total_spend)}</span>
        </DrillCell>
      ) },
    { key: 'first_rate', label: 'First rate', accessor: m => m.first_rate, type: 'number', align: 'right',
      render: m => <span className="text-zinc-400 font-mono">{fmtN(m.first_rate)}</span> },
    { key: 'last_rate', label: 'Latest rate', accessor: m => m.last_rate, type: 'number', align: 'right',
      render: m => <span className="text-zinc-200 font-mono">{fmtN(m.last_rate)}</span> },
    { key: 'delta_pct', label: 'Delta', accessor: m => m.delta_pct, type: 'number', align: 'right',
      render: m => m.delta_pct > 0 ? <span className="text-red-400 font-mono font-semibold">+{m.delta_pct}%</span>
        : m.delta_pct < 0 ? <span className="text-green-400 font-mono font-semibold">{m.delta_pct}%</span>
        : <span className="text-zinc-500 font-mono">0%</span> },
    { key: 'trend', label: 'Trend', accessor: m => m.delta_pct, sortable: false, filterable: false, exportable: false, width: 'w-48',
      render: m => {
        const max = Math.max(1, ...m.series.map(s => s.rate));
        const min = Math.min(...m.series.map(s => s.rate || max));
        const range = max - min || 1;
        return (
          <svg viewBox={`0 0 ${m.series.length * 10} 30`} className="w-full h-6">
            {m.series.length > 1 && (
              <polyline fill="none" stroke="#a855f7" strokeWidth="1.5"
                points={m.series.map((s, i) => `${i * 10 + 2},${28 - ((s.rate - min) / range) * 24}`).join(' ')} />
            )}
            {m.series.map((s, i) => (
              <circle key={i} cx={i * 10 + 2} cy={28 - ((s.rate - min) / range) * 24} r="1.5" fill="#a855f7">
                <title>{`${s.month}: ₹${s.rate}`}</title>
              </circle>
            ))}
          </svg>
        );
      } },
  ];

  // ── Costing Predictions columns ───────────────────────────────────────
  const predictionColumns = [
    { key: 'material', label: 'Material', accessor: p => p.material, type: 'text' },
    { key: 'current_stock', label: 'Current Stock', accessor: p => p.current_stock, type: 'number', align: 'right',
      render: p => <span className="text-sky-300 font-mono">{fmtN(p.current_stock)} {p.unit}</span> },
    { key: 'avg_cost_90d', label: 'Avg Cost (90d)', accessor: p => p.avg_cost_90d, type: 'number', align: 'right',
      render: p => <span className="text-emerald-300 font-mono">{fmt(p.avg_cost_90d)}</span> },
    { key: 'avg_cost_prior_90d', label: 'Prior 90d', accessor: p => p.avg_cost_prior_90d, type: 'number', align: 'right',
      render: p => <span className="text-zinc-500 font-mono">{fmt(p.avg_cost_prior_90d)}</span> },
    { key: 'trend_pct', label: 'Trend', accessor: p => p.trend_pct, type: 'number', align: 'right',
      render: p => p.trend_pct > 0 ? <span className="text-red-400 font-mono font-semibold">↑ {p.trend_pct}%</span>
        : p.trend_pct < 0 ? <span className="text-green-400 font-mono font-semibold">↓ {Math.abs(p.trend_pct)}%</span>
        : <span className="text-zinc-500">—</span> },
    { key: 'suggested_cost_per_unit', label: 'Suggested Use', accessor: p => p.suggested_cost_per_unit, type: 'number', align: 'right',
      render: p => <span className="text-amber-300 font-mono font-bold">{fmt(p.suggested_cost_per_unit)} /{p.unit}</span> },
    { key: 'stock_value_at_avg', label: 'Stock Value', accessor: p => p.stock_value_at_avg, type: 'number', align: 'right',
      render: p => <span className="text-emerald-400 font-mono">{fmt(p.stock_value_at_avg)}</span> },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="procurement-analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Procurement Analytics</h1>
          <p className="text-zinc-500 text-sm mt-1">Vendor ratings · price fluctuation · costing predictions for production planning.</p>
        </div>
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="text-zinc-500">Rolling:</span>
          {[1, 3, 6, 12, 24].map(m => (
            <button key={m}
              onClick={() => { setMonths(m); setPickerYear(null); setPickerMonth(null); }}
              data-testid={`window-${m}`}
              className={`px-2.5 py-1 rounded border ${!pickerYear && months === m ? 'bg-amber-500 text-black border-amber-500 font-semibold' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700'}`}>
              {m}m
            </button>
          ))}
          <span className="text-zinc-700 mx-2">|</span>
          <span className="text-zinc-500">Specific month:</span>
          <input
            type="month"
            data-testid="month-picker"
            value={pickerYear && pickerMonth ? `${pickerYear}-${String(pickerMonth).padStart(2, '0')}` : ''}
            onChange={(e) => {
              if (!e.target.value) { setPickerYear(null); setPickerMonth(null); return; }
              const [y, m] = e.target.value.split('-').map(Number);
              setPickerYear(y); setPickerMonth(m);
            }}
            className={`bg-zinc-900 border rounded px-2 py-1 text-zinc-100 ${pickerYear ? 'border-amber-500' : 'border-zinc-800'}`}
          />
          {pickerYear && pickerMonth && (
            <button onClick={() => { setPickerYear(null); setPickerMonth(null); }}
              data-testid="month-picker-clear"
              className="text-[10px] text-zinc-500 hover:text-zinc-200 px-1">clear</button>
          )}
        </div>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Loading…</div>}

      {!loading && (
        <>
          <DataGrid
            testId="vendor-performance"
            title="Vendor Performance"
            subtitle={`last ${months} months`}
            icon={<Star size={14} className="text-amber-400" />}
            columns={vendorColumns}
            rows={vendors}
            rowKey={(v) => v.vendor}
            initialSort={{ key: 'spend', dir: 'desc' }}
            exportName="vendor-performance"
            emptyText="No vendor data in this window."
          />

          <DataGrid
            testId="price-fluctuation"
            title="Price Fluctuation (Macro)"
            icon={<TrendingUp size={14} className="text-fuchsia-400" />}
            columns={materialColumns}
            rows={materials}
            rowKey={(m) => m.material}
            initialSort={{ key: 'total_spend', dir: 'desc' }}
            exportName="price-fluctuation"
            emptyText="No material price data."
          />

          <DataGrid
            testId="costing-predictions"
            title="Costing Predictions for Production Planning"
            subtitle="90d avg vs prior 90d"
            icon={<Brain size={14} className="text-emerald-400" />}
            columns={predictionColumns}
            rows={predictions}
            rowKey={(p) => p.material}
            initialSort={{ key: 'stock_value_at_avg', dir: 'desc' }}
            exportName="costing-predictions"
            emptyText="No purchase data in last 90 days."
          />
        </>
      )}
    </div>
  );
}
